import { NewsfeedData } from './news-feed.interfaces';

/**
 * Validates if the newsfeed data contains valid content
 */
export function isValidNewsfeedData(
  data: NewsfeedData | null | undefined,
): boolean {
  if (!data) {
    return false;
  }

  // Check for error state first
  if (data.reqError) {
    return false;
  }

  // Check if we have actual content
  const hasNews = Array.isArray(data.news) && data.news.length > 0;
  const hasStories = Array.isArray(data.stories) && data.stories.length > 0;
  const hasEvents = Array.isArray(data.events) && data.events.length > 0;

  return hasNews || hasStories || hasEvents;
}

/**
 * Determines if the newsfeed data represents a loading state
 */
export function isLoadingState(data: NewsfeedData | null | undefined): boolean {
  if (!data) {
    return true;
  }

  // If we have an error, we're not loading
  if (data.reqError) {
    return false;
  }

  // If we have no content and no error, we're likely loading
  const hasContent =
    (Array.isArray(data.news) && data.news.length > 0) ||
    (Array.isArray(data.stories) && data.stories.length > 0) ||
    (Array.isArray(data.events) && data.events.length > 0);

  return !hasContent && Object.keys(data).length === 0;
}

/**
 * Determines if the newsfeed data represents an error state
 */
export function isErrorState(data: NewsfeedData | null | undefined): boolean {
  return data?.reqError === true;
}

/**
 * Determines if the newsfeed data represents an empty portfolio
 * (no entities, so no request was sent)
 */
export function isEmptyPortfolioState(
  data: NewsfeedData | null | undefined,
): boolean {
  return data?.emptyPortfolio === true;
}

/**
 * Determines if the newsfeed data represents an empty state (no results)
 */
export function isEmptyState(data: NewsfeedData | null | undefined): boolean {
  if (!data || data.reqError) {
    return false;
  }

  const hasNews = Array.isArray(data.news) && data.news.length === 0;
  const hasStories = Array.isArray(data.stories) && data.stories.length === 0;
  const hasEvents = Array.isArray(data.events) && data.events.length === 0;

  // If arrays exist but are empty, it's an empty state
  return hasNews || hasStories || hasEvents;
}
